import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { createChart, CandlestickSeries, LineSeries, createSeriesMarkers } from "lightweight-charts";
import { useSwipeClose } from "./useSwipeClose";
import { expiryOf, dteOf, isExpiryDay } from "./expiry";

/* ===== TradeDrawer — right-side drawer with full trade details =====
   Opens for any trade record (live monitor trade or backtest trade).
   Keyboard: F toggles full screen, Esc closes. */

const fmtMoney = (v) => (v == null ? "-" : `₹${Math.abs(v).toLocaleString("en-IN")}`);
const fmtSignedMoney = (v) => (v == null ? "-" : `${v >= 0 ? "+" : "-"}₹${Math.abs(v).toLocaleString("en-IN")}`);
const fmtNum = (v, d = 2) => (v == null || isNaN(v) ? "-" : Number(v).toFixed(d));
const fmtTime = (v) => {
  if (!v) return "-";
  const dt = new Date(v);
  return isNaN(dt) ? String(v) : dt.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
};

function Row({ label, children, accent }) {
  if (children == null || children === "-" || children === "") return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "5px 0", borderBottom: "1px solid var(--panel3)", fontSize: 12.5 }}>
      <span style={{ color: "var(--dim2)", flexShrink: 0 }}>{label}</span>
      <span style={{ color: accent || "var(--text2)", textAlign: "right", fontWeight: 500, wordBreak: "break-word" }}>{children}</span>
    </div>
  );
}

function Section({ title, children }) {
  const has = React.Children.toArray(children).some(Boolean);
  if (!has) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", margin: "0 0 4px", fontWeight: 700 }}>{title}</div>
      {children}
    </div>
  );
}

/* Live 5-min chart of the trade's day (lightweight-charts): wheel/pinch zoom,
   drag-pan, crosshair with OHLC readout, VWAP + EMA20 overlays, entry/SL/target/
   fib price lines, entry & exit markers. */
function DrawerChart({ trade, fullscreen }) {
  const [candles, setCandles] = useState(null);
  const [err, setErr] = useState("");
  const holderRef = useRef(null);

  const tradeTime = trade.time || trade.placedAt;
  const tradeDateStr = tradeTime ? String(new Date(tradeTime).toLocaleDateString("en-CA")) : "";
  const index = trade.index || "NIFTY";

  useEffect(() => {
    let cancelled = false;
    setCandles(null); setErr("");
    if (!tradeDateStr) { setErr("no trade time"); return; }
    const diffDays = Math.max(1, Math.ceil((Date.now() - new Date(tradeTime)) / 86400000));
    axios.get("/api/zerodha/fib-vwap", {
      params: { timeframe: "5minute", days: String(diffDays + 1), vwap: "true", sr: "true", index },
      timeout: 30000,
    }).then(res => {
      if (cancelled) return;
      const rows = (res.data?.data?.chartData || []).filter(c => String(c.date).startsWith(tradeDateStr));
      if (rows.length) setCandles(rows); else setErr("no candles for trade day");
    }).catch(e => { if (!cancelled) setErr(e.message || "failed to load chart"); });
    return () => { cancelled = true; };
  }, [tradeTime, tradeDateStr, index]);

  useEffect(() => {
    if (!candles || !holderRef.current) return;
    const light = document.body.classList.contains("light");
    const col = light
      ? { bg: "#f8fafc", text: "#4c5b68", grid: "#e5eaf0", vwap: "#111111" }
      : { bg: "#0b1120", text: "#64748b", grid: "#1c2536", vwap: "#f2f2f2" };
    const EMA_BLUE = "#2962ff";
    const chartHeight = fullscreen ? Math.max(400, window.innerHeight - 230) : 300;
    const chart = createChart(holderRef.current, {
      width: holderRef.current.clientWidth || 500,
      height: chartHeight,
      layout: { background: { color: col.bg }, textColor: col.text, attributionLogo: false },
      grid: { vertLines: { color: col.grid }, horzLines: { color: col.grid } },
      crosshair: { mode: 0 },
      timeScale: { timeVisible: true, secondsVisible: false, borderColor: col.grid },
      rightPriceScale: { borderColor: col.grid },
    });
    const IST = 19800; // display IST on the (UTC-rendered) time scale
    const toTime = (d) => Math.floor(Date.parse(d) / 1000) + IST;
    const cs = chart.addSeries(CandlestickSeries, {
      upColor: "#22c55e", downColor: "#dc2626", borderVisible: false,
      wickUpColor: "#22c55e", wickDownColor: "#dc2626",
    });
    cs.setData(candles.map(c => ({ time: toTime(c.date), open: c.open, high: c.high, low: c.low, close: c.close })));
    const vwap = chart.addSeries(LineSeries, { color: col.vwap, lineWidth: 1, priceLineVisible: false, lastValueVisible: true, title: "VWAP", crosshairMarkerVisible: false });
    vwap.setData(candles.filter(c => c.vwap != null).map(c => ({ time: toTime(c.date), value: c.vwap })));
    const ema = chart.addSeries(LineSeries, { color: EMA_BLUE, lineWidth: 2, lineStyle: 2, priceLineVisible: false, lastValueVisible: true, title: "EMA20", crosshairMarkerVisible: false });
    ema.setData(candles.filter(c => c.ema20 != null).map(c => ({ time: toTime(c.date), value: c.ema20 })));

    const pl = (price, color, title, style) => { if (price != null) cs.createPriceLine({ price, color, lineWidth: 1, lineStyle: style, title, axisLabelVisible: true }); };
    pl(trade.entry, "#94a3b8", "entry", 0);
    pl(trade.sl, "#dc2626", "SL", 2);
    pl(trade.target, "#22c55e", "target", 2);
    pl(trade.fibPrice, "#a78bfa", "fib " + (trade.fibLevel || ""), 3);
    if (trade.srZone && trade.srZone.center) pl(trade.srZone.center, "#f59e0b", "S/R", 3);

    const nearest = (ts) => {
      let best = null;
      for (const c of candles) { const t = toTime(c.date); if (t <= ts) best = t; }
      return best;
    };
    const markers = [];
    if (tradeTime) {
      const t = nearest(Math.floor(Date.parse(tradeTime) / 1000) + IST);
      if (t) markers.push({ time: t, position: trade.type === "SHORT" ? "aboveBar" : "belowBar", color: trade.type === "SHORT" ? "#dc2626" : "#22c55e", shape: trade.type === "SHORT" ? "arrowDown" : "arrowUp", text: "entry" });
    }
    if (trade.exitTime) {
      const t = nearest(Math.floor(Date.parse(trade.exitTime) / 1000) + IST);
      if (t) markers.push({ time: t, position: "aboveBar", color: "#fbbf24", shape: "circle", text: (trade.exitReason || "exit").toLowerCase() });
    }
    if (markers.length) createSeriesMarkers(cs, markers.sort((a, b) => a.time - b.time));

    chart.timeScale().fitContent();
    // the drawer animates its width; keep the chart synced to the container
    const syncWidth = () => {
      if (!holderRef.current) return;
      chart.applyOptions({ width: holderRef.current.clientWidth });
      chart.timeScale().fitContent();   // re-stretch the candles into the new width
    };
    const ro = new ResizeObserver(syncWidth);
    ro.observe(holderRef.current);
    window.addEventListener("resize", syncWidth);
    return () => { ro.disconnect(); window.removeEventListener("resize", syncWidth); chart.remove(); };
  }, [candles, fullscreen, trade, tradeTime]);

  if (err) return <div style={{ fontSize: 11, color: "var(--muted2)", padding: "6px 0 10px" }}>Chart unavailable ({err})</div>;
  return (
    <div style={{ marginBottom: 14 }}>
      <div ref={holderRef} data-noswipe style={{ width: "100%", height: fullscreen ? Math.max(400, window.innerHeight - 230) : 300, borderRadius: 8, overflow: "hidden", border: "1px solid var(--panel3)", background: "var(--chart-bg)" }}>
        {!candles && <div style={{ fontSize: 11, color: "var(--muted2)", padding: 10 }}>Loading chart…</div>}
      </div>
      <div style={{ display: "flex", gap: 10, fontSize: 9.5, color: "var(--muted2)", marginTop: 4, flexWrap: "wrap" }}>
        <span style={{ color: document.body.classList.contains("light") ? "#111" : "#f2f2f2", fontWeight: 700 }}>— VWAP</span>
        <span style={{ color: "#2962ff", fontWeight: 700 }}>-- EMA20</span>
        <span style={{ color: "#a78bfa" }}>·· Fib</span>
        <span style={{ color: "#94a3b8" }}>— entry / <span style={{ color: "#dc2626" }}>SL</span> / <span style={{ color: "#22c55e" }}>target</span></span>
        <span style={{ marginLeft: "auto" }}>scroll = zoom · drag = pan · {index} 5m {tradeDateStr}</span>
      </div>
    </div>
  );
}

export default function TradeDrawer({ trade, onClose }) {
  const [fullscreen, setFullscreen] = useState(false);
  const swipe = useSwipeClose(onClose);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      else if (e.key === "f" || e.key === "F") { e.preventDefault(); setFullscreen(f => !f); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!trade) return null;
  const t = trade;
  const opt = t.option || {};
  const pnl = t.netPnl != null ? t.netPnl : opt.moneyPnl;
  const isWin = pnl != null && pnl >= 0;
  const entryPrem = t.entryPremium != null ? t.entryPremium : opt.entryPremium;
  const exitPrem = t.exitPremium != null ? t.exitPremium : opt.exitPremium;
  const qty = t.qty != null ? t.qty : opt.qty;
  const symbol = t.tradingsymbol || opt.optionSymbol;
  const shownKeys = new Set([
    "id", "type", "signalType", "strike", "strikeType", "tradingsymbol", "optionToken", "index", "strategy", "status",
    "simulated", "broker", "qty", "lots", "capital", "time", "placedAt", "entry", "sl", "target", "risk", "fibLevel",
    "fibPrice", "vwapPrice", "srZone", "confluence", "signalRationale", "entryPremium", "exitPremium", "targetPremium",
    "grossPnl", "charges", "netPnl", "exitReason", "exitTime", "exitPrice", "orderId", "rMultiple", "result", "pnl",
    "trailExitDone", "trailExitQty", "trailExitPremium", "trailExitPnl", "trailExitReason", "trailExitDate", "trailExitPrice",
    "remainingQty", "option", "oiSnapshot", "vixEntry", "maxPremium", "aiReviews", "aiExitDelta", "expiry", "expiryInferred", "minPremium", "minPremiumAt", "maxPremiumAt", "peakFavorableSpot", "peakFavorableAt", "maxAdverseSpot", "maxAdverseAt",
  ]);
  const aiReviews = Array.isArray(t.aiReviews) ? t.aiReviews : [];
  const extraKeys = Object.keys(t).filter(k => !shownKeys.has(k) && t[k] != null && typeof t[k] !== "object");

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1199 }} />
      <div {...swipe.handlers} style={{
        position: "fixed", top: 0, right: 0, height: "100vh", zIndex: 1200,
        width: fullscreen ? "100vw" : "min(540px, 94vw)",
        background: "var(--panel2)", borderLeft: "1px solid var(--border2)", boxShadow: "-8px 0 30px rgba(0,0,0,0.5)",
        display: "flex", flexDirection: "column",
        transform: swipe.dx ? `translateX(${swipe.dx}px)` : "none",
        transition: swipe.dx ? "none" : "width 0.15s ease, transform 0.15s ease",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "12px 16px", borderBottom: "1px solid var(--panel3)", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text2)" }}>
              <span className={`signal-badge ${t.type === "SHORT" ? "badge-short" : "badge-long"}`} style={{ marginRight: 8 }}>{t.type}</span>
              {t.signalType || opt.type} {t.strike || opt.strike} {symbol ? `· ${symbol}` : ""}
            </div>
            <div style={{ fontSize: 11, color: "var(--muted2)", marginTop: 2 }}>
              {t.id != null && <span style={{ marginRight: 8, fontWeight: 700, color: "var(--dim2)" }} title="Trade id">#{t.id}</span>}
              {fmtTime(t.time || t.placedAt || t.exactEntryTime || t.triggerDate)}
              {t.status && <span style={{ marginLeft: 8, color: t.status === "OPEN" ? "#fbbf24" : "var(--dim2)", fontWeight: 700 }}>{t.status}</span>}
              {pnl != null && <span style={{ marginLeft: 8, color: isWin ? "#34d399" : "#f87171", fontWeight: 700 }}>{fmtSignedMoney(pnl)}</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
            <span style={{ fontSize: 10, color: "var(--faint2)", marginRight: 4 }}>F full screen · Esc close</span>
            <button onClick={() => setFullscreen(f => !f)} title="Toggle full screen (F)"
              style={{ background: "var(--panel3)", color: "var(--text2)", border: "1px solid var(--border2)", borderRadius: 4, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>
              {fullscreen ? "⇥ Exit Full" : "⇤ Full"}
            </button>
            <button onClick={onClose} title="Close (Esc)"
              style={{ background: "var(--panel3)", color: "var(--text2)", border: "1px solid var(--border2)", borderRadius: 4, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>
              ✕
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: "12px 16px", flex: 1 }}>
          <DrawerChart trade={t} fullscreen={fullscreen} />
          <div style={{ ...(fullscreen ? { columnCount: 2, columnGap: 40 } : {}) }}>
          <div style={{ breakInside: "avoid" }}>
          <Section title="Position">
            <Row label="Index / Strategy">{[t.index, t.strategy].filter(Boolean).join(" · ")}</Row>
            <Row label="Contract">{symbol}</Row>
            <Row label="Strike">{(t.strike || opt.strike) != null ? `${t.strike || opt.strike} ${t.strikeType || ""}` : null}</Row>
            <Row label="Qty / Lots">{qty != null ? `${qty}${t.lots ? ` (${t.lots} lots)` : ""}` : null}</Row>
            <Row label="Capital deployed">{t.capital != null ? fmtMoney(t.capital) : null}</Row>
            <Row label="Contract expiry" accent={isExpiryDay(t) ? "var(--warn-text)" : undefined}>{expiryOf(t) ? `${expiryOf(t)}${t.expiryInferred ? " (inferred)" : ""}${isExpiryDay(t) ? " — EXPIRY-DAY TRADE" : ""}` : null}</Row>
            <Row label="DTE at entry">{dteOf(t) != null ? `${dteOf(t)} day${dteOf(t) === 1 ? "" : "s"}` : null}</Row>
            <Row label="Mode">{t.simulated != null ? (t.simulated ? "SIM (paper)" : "LIVE") : null}</Row>
            <Row label="Order ID">{t.orderId}</Row>
          </Section>
          <Section title="Levels (spot)">
            <Row label="Entry">{t.entry != null ? fmtNum(t.entry, 1) : null}</Row>
            <Row label="Stop loss" accent="#f87171">{t.sl != null ? fmtNum(t.sl, 1) : null}</Row>
            <Row label="Target" accent="#34d399">{t.target != null ? fmtNum(t.target, 1) : null}</Row>
            <Row label="Risk (pts)">{t.risk != null ? fmtNum(t.risk, 1) : null}</Row>
            <Row label="R multiple">{t.rMultiple != null ? `${t.rMultiple >= 0 ? "+" : ""}${fmtNum(t.rMultiple)}R` : null}</Row>
          </Section>
          <Section title="Signal">
            <Row label="Fib level">{t.fibLevel ? `${t.fibLevel}${t.fibPrice ? ` @ ${fmtNum(t.fibPrice, 1)}` : ""}` : null}</Row>
            <Row label="VWAP">{t.vwapPrice != null ? fmtNum(t.vwapPrice, 1) : null}</Row>
            <Row label="S/R zone">{t.srZone ? `${fmtNum(t.srZone.center, 1)} (${t.srZone.touches} touches)` : null}</Row>
            <Row label="Confluence">{Array.isArray(t.confluence) && t.confluence.length ? t.confluence.join(" + ") : null}</Row>
            <Row label="India VIX at entry">{t.vixEntry != null ? Number(t.vixEntry).toFixed(2) : null}</Row>
            {t.signalRationale && (
              <div style={{ fontSize: 12, color: "var(--dim3)", background: "var(--panel3)", borderRadius: 6, padding: "8px 10px", marginTop: 6, lineHeight: 1.5 }}>
                {t.signalRationale}
              </div>
            )}
          </Section>
          </div>
          <div style={{ breakInside: "avoid" }}>
          <Section title="Premiums & P&L">
            <Row label="Entry premium">{entryPrem != null ? `₹${fmtNum(entryPrem, 2)}` : null}</Row>
            <Row label="Exit premium">{exitPrem != null ? `₹${fmtNum(exitPrem, 2)}` : null}</Row>
            <Row label="Est. premium @ target">{t.targetPremium != null ? `₹${fmtNum(t.targetPremium, 1)}` : null}</Row>
            <Row label="Peak premium seen">{t.maxPremium != null ? `₹${fmtNum(t.maxPremium, 2)}` : null}</Row>
            <Row label="Worst premium seen" accent="#f87171">{t.minPremium != null ? `₹${fmtNum(t.minPremium, 2)}` : null}</Row>
            <Row label="Peak favorable (spot)">{t.peakFavorableSpot ? `+${fmtNum(t.peakFavorableSpot, 1)} pts` : null}</Row>
            <Row label="Max adverse (spot)" accent="#f87171">{t.maxAdverseSpot ? `-${fmtNum(t.maxAdverseSpot, 1)} pts` : null}</Row>
            <Row label="Gross P&L" accent={(t.grossPnl ?? opt.moneyPnlGross) >= 0 ? "#34d399" : "#f87171"}>
              {(t.grossPnl != null || opt.moneyPnlGross != null) ? fmtSignedMoney(t.grossPnl != null ? t.grossPnl : opt.moneyPnlGross) : null}
            </Row>
            <Row label="Charges">{(t.charges != null || opt.charges?.totalCharges != null) ? fmtMoney(typeof t.charges === "number" ? t.charges : opt.charges?.totalCharges) : null}</Row>
            <Row label="Net P&L" accent={isWin ? "#34d399" : "#f87171"}>{pnl != null ? fmtSignedMoney(pnl) : null}</Row>
            <Row label="Spot P&L (pts)">{t.pnl != null && t.netPnl == null ? `${t.pnl >= 0 ? "+" : ""}${fmtNum(t.pnl, 1)}` : null}</Row>
          </Section>
          <Section title="Partial exits / tiers">
            <Row label="Tier booked">{t.trailExitDone ? `${t.trailExitQty || "-"} qty @ ₹${fmtNum(t.trailExitPremium, 2)}` : null}</Row>
            <Row label="Tier P&L" accent={t.trailExitPnl >= 0 ? "#34d399" : "#f87171"}>{t.trailExitPnl != null ? fmtSignedMoney(t.trailExitPnl) : null}</Row>
            <Row label="Tier reason">{t.trailExitReason}</Row>
            <Row label="Tier time">{t.trailExitDate ? fmtTime(t.trailExitDate) : null}</Row>
            <Row label="Still running">{t.remainingQty != null ? `${t.remainingQty} qty` : null}</Row>
          </Section>
          <Section title="Exit">
            <Row label="Reason">{t.exitReason}</Row>
            <Row label="Exit spot">{t.exitPrice != null ? fmtNum(t.exitPrice, 1) : null}</Row>
            <Row label="Exit time">{t.exitTime ? fmtTime(t.exitTime) : null}</Row>
            <Row label="Result">{t.result}</Row>
          </Section>
          {aiReviews.length > 0 && (
            <Section title="AI review (advisory)">
              {aiReviews.map((r, i) => (
                <div key={i} style={{ background: "var(--panel3)", borderRadius: 6, padding: "8px 10px", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "1px 7px",
                      background: r.verdict === "EXIT" ? "rgba(248,113,113,0.15)" : "rgba(52,211,153,0.12)",
                      color: r.verdict === "EXIT" ? "#f87171" : "#34d399",
                    }}>🤖 {r.verdict}{r.confidence != null ? ` ${r.confidence}%` : ""}</span>
                    <span style={{ fontSize: 10.5, color: "var(--muted2)" }}>{r.detector}</span>
                    <span style={{ fontSize: 10.5, color: "var(--faint2)" }}>{fmtTime(r.time)}</span>
                  </div>
                  {r.detail && <div style={{ fontSize: 11, color: "var(--dim2)", marginTop: 4 }}>{r.detail}</div>}
                  {r.reason && <div style={{ fontSize: 11.5, color: "var(--dim3)", marginTop: 4, lineHeight: 1.45 }}>{r.reason}</div>}
                  {r.premium != null && <div style={{ fontSize: 10.5, color: "var(--muted2)", marginTop: 4 }}>Premium at review: ₹{fmtNum(r.premium, 2)}</div>}
                </div>
              ))}
              {t.aiExitDelta != null && (
                <div style={{ fontSize: 11.5, fontWeight: 600, marginTop: 2, color: t.aiExitDelta >= 0 ? "#34d399" : "#f87171" }}>
                  Following the AI exit would have {t.aiExitDelta >= 0 ? "saved" : "cost"} {fmtMoney(t.aiExitDelta)}
                </div>
              )}
            </Section>
          )}
          {extraKeys.length > 0 && (
            <Section title="Other fields">
              {extraKeys.map(k => <Row key={k} label={k}>{String(t[k])}</Row>)}
            </Section>
          )}
          <details style={{ marginTop: 8 }}>
            <summary style={{ fontSize: 11, color: "var(--muted2)", cursor: "pointer" }}>Raw record (JSON)</summary>
            <pre style={{ fontSize: 10.5, color: "var(--dim2)", background: "var(--panel3)", borderRadius: 6, padding: 10, overflowX: "auto", marginTop: 6 }}>
              {JSON.stringify(t, null, 2)}
            </pre>
          </details>
          </div>
          </div>
        </div>
      </div>
    </>
  );
}
